import { useEffect, useMemo, useState, useCallback } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';
import {
  type SeamManifestDto,
  seamCapabilityAuthorized,
} from '@/lib/pluginSeam';

/**
 * Story 121.4 / 121.7 — per-tenant module capabilities hook.
 *
 * Fetches `/api/v1/me/capabilities` on auth change and exposes:
 *
 *   - `has(key)`  : convenience predicate for nav / route filtering.
 *   - `effective` : Set<string> of effective module keys (entitlement
 *                   ∧ activation, per the resolver — authorization is
 *                   layered on by the endpoint's @PreAuthorize).
 *   - `states`    : full per-module state (used by the storefronts
 *                   121.9 / 121.10 to render purchasable upsells).
 *   - `loading`   : true while the first fetch is in flight.
 *
 * Foundation modules (key prefix `core.`) are always present in the
 * effective set; gating against them is allowed but always passes.
 *
 * Mirrors `usePermissions` (45.6) so the two read fine side-by-side in
 * `AppShell.tsx`.
 */
export interface ModuleStateDto {
  key: string;
  name: string;
  foundation: boolean;
  tierFloor: 'FREE' | 'STARTER' | 'PRO' | 'ENTERPRISE';
  purchasable: boolean;
  displayOrder: number;
  entitled: boolean;
  activated: boolean;
  effective: boolean;
  source: 'FOUNDATION' | 'TIER' | 'GRANT' | 'PURCHASE' | null;
}

export interface MeCapabilitiesDto {
  tenantId: string;
  subscriptionTier: string;
  states: ModuleStateDto[];
  /**
   * VYV-K1.4 — versioned plugin-seam manifest: which Kairos capabilities the
   * current tenant/role may operate. Absent on the client-portal persona and
   * on backends predating the seam. See `@/lib/pluginSeam`.
   */
  seam?: SeamManifestDto;
}

export function useCapabilities() {
  const tokens = useAuthStore((s) => s.tokens);
  const [data, setData] = useState<MeCapabilitiesDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tokens) {
      setData(null);
      return;
    }
    setLoading(true);
    apiClient
      .get<MeCapabilitiesDto>('/me/capabilities')
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tokens]);

  const effective = useMemo(() => {
    // `states` can be absent/null when the payload carries no modules
    // (e.g. a tenant with none provisioned serializes a nil slice as
    // JSON null) — guard so a null bundle never white-screens the shell.
    if (!data?.states) return new Set<string>();
    return new Set(data.states.filter((s) => s.effective).map((s) => s.key));
  }, [data]);

  const has = useCallback(
    (key: string): boolean => {
      // PLATFORM_ADMIN bypass is handled server-side via the @capabilities
      // bean's service-principal short-circuit, so we don't need to repeat
      // it here. While loading, default to true so the shell does not
      // flash an empty nav on first render — a brief over-show beats a
      // brief blank state for UX. We also fail OPEN when the payload
      // carries no `states` bundle (older/alternate capabilities response
      // shapes): nav stays visible and the server still enforces
      // authorization on access — a brief over-show beats hiding the app.
      if (loading || !data?.states) return true;
      return effective.has(key);
    },
    [data, loading, effective],
  );

  // VYV-K1.4 — `canOperate(capability)` answers "may the vertical operate this
  // Kairos capability now?" from the seam manifest. It fails CLOSED (false)
  // while loading or when no manifest is present: unlike module-nav `has`
  // (optimistic for UX), offering a Kairos operation the principal cannot
  // perform is worse than briefly not offering it — and the backend enforces
  // the same gate on any direct call regardless.
  const canOperate = useCallback(
    (capability: string): boolean => seamCapabilityAuthorized(data?.seam, capability),
    [data],
  );

  return { has, canOperate, effective, seam: data?.seam, states: data?.states ?? [], data, loading };
}
